/**
 * Gruppkraft — "Så fungerar det" scrollytelling
 *
 * Kräver GSAP + ScrollTrigger (självhostade tema-assets, laddas i
 * sektionsfilen). Progressive enhancement:
 *   - Om GSAP saknas eller användaren föredrar reducerad rörelse initieras
 *     ingenting — sektionen förblir statisk och läsbar.
 *   - Två lägen via gsap.matchMedia():
 *       Desktop (>900px): scenen är fäst (CSS sticky) och en scrubbad
 *       ScrollTrigger mappar scrollprogress → aktivt steg, progress-linje
 *       och parallax-lager. Varje scrollrörelse tar besökaren vidare.
 *       Mobil (<=900px): varje steg aktiveras när det passerar mitten av
 *       vyn medan sidan scrollas (panelen ligger kvar i flödet).
 */
(function () {
  "use strict";

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");

  function initAll() {
    document.querySelectorAll("[data-scrolly]").forEach(initSection);
  }

  function initSection(root) {
    if (root.dataset.scrollyInit === "1") return;
    root.dataset.scrollyInit = "1";

    var steps = Array.prototype.slice.call(
      root.querySelectorAll("[data-scrolly-step]")
    );
    var visuals = Array.prototype.slice.call(
      root.querySelectorAll("[data-scrolly-visual]")
    );
    var fill = root.querySelector("[data-scrolly-fill]");
    var depthLayers = Array.prototype.slice.call(
      root.querySelectorAll("[data-depth]")
    );
    var count = steps.length;
    if (!count) return;

    if (REDUCED.matches || !window.gsap || !window.ScrollTrigger) {
      return; // statisk fallback
    }

    gsap.registerPlugin(ScrollTrigger);
    root.classList.add("scrolly--ready");

    var current = -1;

    function setActive(index) {
      if (index === current) return;
      current = index;

      steps.forEach(function (step, i) {
        step.classList.toggle("is-active", i === index);
        step.classList.toggle("is-done", i < index);
        var btn = step.querySelector("[data-scrolly-jump]");
        if (btn) {
          if (i === index) {
            btn.setAttribute("aria-current", "step");
          } else {
            btn.removeAttribute("aria-current");
          }
        }
      });

      visuals.forEach(function (visual, i) {
        if (i === index) {
          visual.classList.add("is-active");
          gsap.fromTo(
            visual,
            { opacity: 0, scale: 0.94, yPercent: 3 },
            {
              opacity: 1,
              scale: 1,
              yPercent: 0,
              duration: 0.7,
              ease: "power3.out",
              overwrite: "auto",
            }
          );
          var chip = visual.querySelector("[data-scrolly-icon]");
          if (chip) {
            gsap.fromTo(
              chip,
              { scale: 0.6, rotate: -8 },
              {
                scale: 1,
                rotate: 0,
                duration: 0.9,
                ease: "elastic.out(1, 0.55)",
                overwrite: "auto",
              }
            );
          }
        } else if (visual.classList.contains("is-active")) {
          visual.classList.remove("is-active");
          gsap.to(visual, {
            opacity: 0,
            scale: 0.97,
            duration: 0.35,
            ease: "power2.in",
            overwrite: "auto",
          });
        }
      });
    }

    var mm = gsap.matchMedia();

    /* Desktop: fäst scen, scrollen driver berättelsen. */
    mm.add("(min-width: 901px)", function () {
      var trigger = ScrollTrigger.create({
        trigger: root,
        start: "top top",
        end: "bottom bottom",
        onUpdate: function (self) {
          if (fill) gsap.set(fill, { scaleY: self.progress });

          var index = Math.min(count - 1, Math.floor(self.progress * count));
          setActive(index);

          // Parallax: lager rör sig olika mycket beroende på djup
          depthLayers.forEach(function (layer) {
            var depth = parseFloat(layer.dataset.depth) || 0;
            gsap.set(layer, { y: (self.progress - 0.5) * depth * 60 });
          });
        },
      });

      setActive(0);
      if (fill) gsap.set(fill, { scaleY: 0 });

      return function () {
        trigger.kill();
      };
    });

    /* Mobil: varje steg aktiveras när det passerar mitten av vyn. */
    mm.add("(max-width: 900px)", function () {
      var triggers = steps.map(function (step, i) {
        return ScrollTrigger.create({
          trigger: step,
          start: "top 60%",
          end: "bottom 60%",
          onEnter: function () {
            setActive(i);
            if (fill) {
              gsap.to(fill, {
                scaleY: (i + 1) / count,
                duration: 0.5,
                ease: "power2.out",
              });
            }
          },
          onEnterBack: function () {
            setActive(i);
            if (fill) {
              gsap.to(fill, {
                scaleY: (i + 1) / count,
                duration: 0.5,
                ease: "power2.out",
              });
            }
          },
        });
      });

      setActive(0);

      return function () {
        triggers.forEach(function (t) {
          t.kill();
        });
      };
    });

    /* Micro-interaction: klick på ett stegnummer hoppar dit i berättelsen. */
    steps.forEach(function (step, i) {
      var btn = step.querySelector("[data-scrolly-jump]");
      if (!btn) return;
      btn.addEventListener("click", function () {
        if (window.matchMedia("(min-width: 901px)").matches) {
          var total = root.offsetHeight - window.innerHeight;
          var top =
            root.getBoundingClientRect().top +
            window.pageYOffset +
            total * ((i + 0.5) / count);
          window.scrollTo({ top: top, behavior: "smooth" });
        } else {
          step.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  /* Theme Editor: initiera om när sektionen laddas om i customizern */
  document.addEventListener("shopify:section:load", function (event) {
    var el = event.target.querySelector("[data-scrolly]");
    if (el) {
      delete el.dataset.scrollyInit;
      initSection(el);
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    }
  });
})();
